import {
	fetchLeadProviders,
	fetchLeadProvider,
	createLeadProvider,
	updateLeadProvider,
	removeLeadProvider,
	fetchLeadMappings,
	createLeadMapping,
	updateLeadMapping,
	removeLeadMapping,
	fetchLeadAliases,
	createLeadAlias,
	removeLeadAlias,
	fetchLeadMailboxes,
	createLeadMailbox,
	updateLeadMailbox,
	removeLeadMailbox,
	fetchLeadMailboxMessages,
	parseLeadMailboxMessages,
	fetchLeadSubscriptions,
	ensureLeadSubscriptions,
	removeLeadSubscription,
	fetchLeadProcessed,
	replayLeadProcessed
} from 'src/api/leadsApi';
import * as actionTypes from '../actions';

const errorToast = (dispatch, error, fallback) =>
	dispatch(
		actionTypes.showToast({
			type: 'error',
			body: error?.response?.data?.message || fallback
		})
	);

// Providers -----------------------------------------------------------------
export const fetchLeadProvidersRequest = () => async (dispatch) => {
	await dispatch(actionTypes.leadProvidersPending());
	try {
		const { data } = await fetchLeadProviders();
		await dispatch(actionTypes.leadProvidersSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadProvidersError(error));
		errorToast(dispatch, error, 'Get lead providers error');
	}
};

export const fetchLeadProviderRequest = (id) => async (dispatch) => {
	try {
		const { data } = await fetchLeadProvider(id);
		await dispatch(actionTypes.leadProviderSuccess(data));
		return data;
	} catch (error) {
		errorToast(dispatch, error, 'Get lead provider error');
		return null;
	}
};

export const createLeadProviderRequest = (body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await createLeadProvider(body);
		await dispatch(fetchLeadProvidersRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Lead provider created' }));
	} catch (error) {
		errorToast(dispatch, error, 'Create lead provider error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const updateLeadProviderRequest = (id, body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await updateLeadProvider(id, body);
		await dispatch(fetchLeadProvidersRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Lead provider updated' }));
	} catch (error) {
		errorToast(dispatch, error, 'Update lead provider error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const removeLeadProviderRequest = (id) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await removeLeadProvider(id);
		await dispatch(fetchLeadProvidersRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Lead provider deleted' }));
	} catch (error) {
		errorToast(dispatch, error, 'Delete lead provider error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

// Mappings ------------------------------------------------------------------
export const fetchLeadMappingsRequest = (providerId) => async (dispatch) => {
	await dispatch(actionTypes.leadMappingsPending());
	try {
		const { data } = await fetchLeadMappings(providerId);
		await dispatch(actionTypes.leadMappingsSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadMappingsError(error));
		errorToast(dispatch, error, 'Get lead mappings error');
	}
};

export const createLeadMappingRequest = (providerId, body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await createLeadMapping(providerId, body);
		await dispatch(fetchLeadMappingsRequest(providerId));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mapping created' }));
	} catch (error) {
		errorToast(dispatch, error, 'Create mapping error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const updateLeadMappingRequest = (id, providerId, body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await updateLeadMapping(id, body);
		await dispatch(fetchLeadMappingsRequest(providerId));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mapping updated' }));
	} catch (error) {
		errorToast(dispatch, error, 'Update mapping error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const removeLeadMappingRequest = (id, providerId) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await removeLeadMapping(id);
		await dispatch(fetchLeadMappingsRequest(providerId));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mapping deleted' }));
	} catch (error) {
		errorToast(dispatch, error, 'Delete mapping error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

// Aliases -------------------------------------------------------------------
export const fetchLeadAliasesRequest = (source) => async (dispatch) => {
	await dispatch(actionTypes.leadAliasesPending());
	try {
		const { data } = await fetchLeadAliases(source);
		await dispatch(actionTypes.leadAliasesSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadAliasesError(error));
		errorToast(dispatch, error, 'Get aliases error');
	}
};

export const createLeadAliasRequest = (body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await createLeadAlias(body);
		await dispatch(fetchLeadAliasesRequest(body.source));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Alias created' }));
	} catch (error) {
		errorToast(dispatch, error, 'Create alias error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const removeLeadAliasRequest = (id, source) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await removeLeadAlias(id);
		await dispatch(fetchLeadAliasesRequest(source));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Alias deleted' }));
	} catch (error) {
		errorToast(dispatch, error, 'Delete alias error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

// Mailboxes -----------------------------------------------------------------
export const fetchLeadMailboxesRequest = () => async (dispatch) => {
	await dispatch(actionTypes.leadMailboxesPending());
	try {
		const { data } = await fetchLeadMailboxes();
		await dispatch(actionTypes.leadMailboxesSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadMailboxesError(error));
		errorToast(dispatch, error, 'Get mailboxes error');
	}
};

export const createLeadMailboxRequest = (body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await createLeadMailbox(body);
		await dispatch(fetchLeadMailboxesRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mailbox created' }));
	} catch (error) {
		errorToast(dispatch, error, 'Create mailbox error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const updateLeadMailboxRequest = (id, body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await updateLeadMailbox(id, body);
		await dispatch(fetchLeadMailboxesRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mailbox updated' }));
	} catch (error) {
		errorToast(dispatch, error, 'Update mailbox error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const removeLeadMailboxRequest = (id) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await removeLeadMailbox(id);
		await dispatch(fetchLeadMailboxesRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Mailbox deleted' }));
	} catch (error) {
		errorToast(dispatch, error, 'Delete mailbox error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const fetchLeadMailboxMessagesRequest = (id, params) => async (dispatch) => {
	await dispatch(actionTypes.leadMailboxMessagesPending());
	try {
		const { data } = await fetchLeadMailboxMessages(id, params);
		await dispatch(actionTypes.leadMailboxMessagesSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadMailboxMessagesError(error));
		errorToast(dispatch, error, 'Get mailbox messages error');
	}
};

export const parseLeadMailboxMessagesRequest = (id, body) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		const { data } = await parseLeadMailboxMessages(id, body);
		await dispatch(
			fetchLeadMailboxMessagesRequest(id, {
				providerId: body.providerId,
				from: body.from,
				to: body.to
			})
		);
		const t = data?.totals || {};
		await dispatch(
			actionTypes.showToast({
				type: t.errors ? 'error' : 'success',
				body: `Parsed ${t.processed || 0}, already processed ${t.duplicates || 0}, skipped ${
					t.skipped || 0
				}, dead-lettered ${t.deadLettered || 0}, errors ${t.errors || 0}`
			})
		);
		return data;
	} catch (error) {
		errorToast(dispatch, error, 'Parse mailbox messages error');
		throw error;
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

// Subscriptions -------------------------------------------------------------
export const fetchLeadSubscriptionsRequest = () => async (dispatch) => {
	try {
		const { data } = await fetchLeadSubscriptions();
		await dispatch(actionTypes.leadSubscriptionsSuccess(data));
	} catch (error) {
		errorToast(dispatch, error, 'Get subscriptions error');
	}
};

export const ensureLeadSubscriptionsRequest = () => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await ensureLeadSubscriptions();
		await dispatch(fetchLeadMailboxesRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Subscriptions ensured' }));
	} catch (error) {
		errorToast(dispatch, error, 'Ensure subscriptions error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

export const removeLeadSubscriptionRequest = (id) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await removeLeadSubscription(id);
		await dispatch(fetchLeadMailboxesRequest());
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Subscription deleted' }));
	} catch (error) {
		errorToast(dispatch, error, 'Delete subscription error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};

// Processed / monitor -------------------------------------------------------
export const fetchLeadProcessedRequest = (params) => async (dispatch) => {
	await dispatch(actionTypes.leadProcessedPending());
	try {
		const { data } = await fetchLeadProcessed(params);
		await dispatch(actionTypes.leadProcessedSuccess(data));
	} catch (error) {
		await dispatch(actionTypes.leadProcessedError(error));
		errorToast(dispatch, error, 'Get processed leads error');
	}
};

export const replayLeadProcessedRequest = (id, params) => async (dispatch) => {
	await dispatch(actionTypes.setLoader(true));
	try {
		await replayLeadProcessed(id);
		await dispatch(fetchLeadProcessedRequest(params));
		await dispatch(actionTypes.showToast({ type: 'success', body: 'Lead replayed' }));
	} catch (error) {
		errorToast(dispatch, error, 'Replay lead error');
	} finally {
		await dispatch(actionTypes.setLoader(false));
	}
};
